import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { colors } from "nxtconstants";



const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= breakpoint);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [breakpoint]);

  return isMobile;
};

const PartnerPriority = ({ priorityData = {} }) => {
  const { heading, cards: cardData = [] } = priorityData;
  const isMobile = useIsMobile();

  const [animateCards, setAnimateCards] = useState(false);
  const [visibleCards, setVisibleCards] = useState(cardData.map(() => false));

  const sectionRef = useRef(null);
  const containerRef = useRef(null);
  const cardRefs = useRef([]);

  const handleScroll = () => {
    if (!sectionRef.current) return;

    const rect = sectionRef.current.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.8) {
      setAnimateCards(true);
    }
  };

  useEffect(() => {
    if (isMobile) return;

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) return;

    setVisibleCards(cardData.map(() => false));

    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = cardRefs.current.indexOf(entry.target);
            if (idx !== -1) {
              setVisibleCards((prev) => {
                const updated = [...prev];
                updated[idx] = true;
                return updated;
              });
              observer.unobserve(entry.target);
            }
          }
        });
      },
      { root: null, rootMargin: "0px", threshold: 0.3 }
    );

    cardRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [isMobile]);

  return (
    <SectionContainer ref={sectionRef} $isMobile={isMobile}>
      <StickyContainer ref={containerRef} $isMobile={isMobile}>
        <ContentWrapper $isMobile={isMobile}>
          <MainTitle>{heading}</MainTitle>
          <MainContent $isMobile={isMobile}>
            {isMobile ? (
              <MobileCardsContainer>
                {cardData.map((card) => {
                  const content = card.mobile;
                  return (
                    <MobileCard key={card.id} $cardColor={card.cardColor}>
                      <MobileCardIcon>
                        <MobileCardIconImage
                          src={card.iconImage}
                          alt={content.title}
                        />
                      </MobileCardIcon>
                      <MobileCardContent>
                        <MobileCardSubtitle>
                          {content.subtitle}
                        </MobileCardSubtitle>
                        <MobileCardTitle>{content.title}</MobileCardTitle>
                        <MobileCardDescription>
                          {content.description}
                        </MobileCardDescription>
                      </MobileCardContent>
                    </MobileCard>
                  );
                })}
              </MobileCardsContainer>
            ) : (
              <ExpandedCardsContainer>
                {cardData.map((card, index) => {
                  const content = card.desktop;
                  const isEven = index % 2 === 1;

                  return (
                    <ExpandedCard
                      key={card.id}
                      ref={(el) => (cardRefs.current[index] = el)}
                      $cardColor={card.cardColor}
                      $isEven={isEven}
                      $isVisible={visibleCards[index]}
                    >
                      <ExpandedCardImage>
                        <ExpandedImage src={card.image} alt={content.title} />
                      </ExpandedCardImage>

                      <ExpandedCardContent>
                        <CardIcon>
                          <CardIconImage
                            src={card.iconImage}
                            alt={content.title}
                          />
                        </CardIcon>
                        <CardContent>
                          <CardSubtitle>{content.subtitle}</CardSubtitle>
                          <CardTitle>{content.title}</CardTitle>
                          <CardDescription>
                            {content.description}
                          </CardDescription>
                        </CardContent>
                      </ExpandedCardContent>
                    </ExpandedCard>
                  );
                })}
              </ExpandedCardsContainer>
            )}
          </MainContent>
        </ContentWrapper>
      </StickyContainer>
    </SectionContainer>
  );
};

const SectionContainer = styled.div`
  position: relative;
  padding: ${(props) => (props.$isMobile ? "4rem 0" : "6rem 0")};
  background: ${colors.white};
  height: auto;

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const StickyContainer = styled.div`
  height: auto;
  position: relative;
`;

const ContentWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: ${colors.white};
`;

const MainTitle = styled.h2`
  font-family: "DM Serif Display";
  font-weight: 400;
  font-size: 5rem;
  color: ${colors.textBlack};
  text-align: center;
  margin: 0rem 0 6rem 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.7rem;
    width: 70%;
    margin: 0 auto;
    margin-bottom: 3rem;
  }
`;

const MainContent = styled.div`
  display: block;
  flex: 1;
  align-items: center;
  max-width: 140rem;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 768px) {
    width: 85%;
    max-width: 125rem;
    margin: 0 auto;
  }
`;

const ExpandedCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 4rem;
  max-width: 125rem;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 0 2rem;
    gap: 3rem;
    width: 40rem;
  }
`;

const ExpandedCard = styled.div`
  background: ${(props) => props.$cardColor || colors.secondary};
  border-radius: 2.4rem;
  padding: 4rem;
  display: flex;
  align-items: center;
  min-height: 35rem;
  gap: 3rem;
  flex-direction: ${(props) => (props.$isEven ? "row-reverse" : "row")};
  
  opacity: ${(props) => (props.$isVisible ? 1 : 0)};
  transform: ${(props) =>
    props.$isVisible
      ? "translateX(0)"
      : `translateX(${props.$isEven ? "100px" : "-100px"})`
  };
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;

  @media (max-width: 768px) {
    padding: 3rem;
    gap: 3rem;
    min-height: 30rem;
    flex-direction: column;
    width: 37rem;
  }
`;

const ExpandedCardImage = styled.div`
  flex: 1;
  width: 100%;
  border-radius: 1.6rem;
  overflow: hidden;
  max-width: 40rem;

  @media (max-width: 768px) {
    height: 22rem;
  }
`;

const ExpandedImage = styled.img`
  width: 40rem;
  object-fit: cover;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ExpandedCardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const MobileCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
`;

const MobileCard = styled.div`
  background: ${(props) => props.$cardColor || colors.secondary};
  border-radius: 1.6rem;
  padding: 2.4rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: none;
  border: none;
`;

const MobileCardIcon = styled.div`
  width: 4rem;
  height: 4rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const MobileCardIconImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const MobileCardContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const MobileCardSubtitle = styled.p`
  font-size: 1.6rem;
  color: ${colors.gray};
  margin: 0 0 1rem 0;
  font-weight: 600;
  font-family: "Lato";
`;

const MobileCardTitle = styled.h3`
  font-size: 2.7rem;
  font-family: "DM Serif Display";
  font-weight: 400;
  color: ${colors.textBlack};
  margin: 0 0 1rem 0;
  line-height: 1.3;
`;

const MobileCardDescription = styled.p`
  font-size: 1.4rem;
  font-family: "Lato";
  color: ${colors.gray};
  font-weight: 400;
  margin: 0;
  line-height: 1.5;
`;

const CardContent = styled.div`
  flex: 1;
`;

const CardIcon = styled.div`
  width: 7rem;
  height: 7rem;
  margin-bottom: 6rem;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    width: 4rem;
    height: 4rem;
    margin-bottom: 2rem;
  }
`;

const CardIconImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const CardSubtitle = styled.p`
  font-size: 2.4rem;
  color: ${colors.gray};
  margin: 0 0 1.2rem 0;
  font-weight: 400;
  letter-spacing: 0.05em;
  font-family: "Lato";

  @media (max-width: 768px) {
    font-size: 1.6rem;
    font-weight: 600;
    margin-bottom: 0.8rem;
  }
`;

const CardTitle = styled.h3`
  font-size: 4.2rem;
  font-family: "DM Serif Display";
  font-weight: 400;
  color: ${colors.textBlack};
  margin: 0 0 1.2rem 0;
  line-height: 1.2;
  letter-spacing: -0.01em;

  @media (max-width: 768px) {
    font-size: 2.4rem;
    margin-bottom: 1rem;
    line-height: 1.3;
  }
`;

const CardDescription = styled.p`
  font-size: 2rem;
  font-family: "Lato";
  color: ${colors.gray};
  font-weight: 400;
  margin: 0;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 1.4rem;
    line-height: 1.5;
  }
`;

export default PartnerPriority;